import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { matchPath } from 'react-router-dom';

import { redirectToVulnDetail, loadVulnerabilityDetail, selectRow, unSelectAll } from 'store/Manage/actions';
import { selectShowQuickPlanner } from 'store/Manage/selectors';
import { selectPathname } from 'store/Router/selectors';
import { selectCurrentWorkspace } from 'store/Faraday/selectors';
import { selectWorkspaces } from 'store/Workspace/selectors';
import itemTypes from 'Common/Components/DragAndDrop/ItemTypes';
import Skeleton from 'Common/Components/Skeleton';
import { getVulnId } from 'Common/Functions/vulnerabilityId';

import { exploreGroup } from 'store/GroupVulns/actions';
import {
  Text, Wrapper,
  GoToButton, GoToIcon,
  Actions
} from './styled';

const Name = ({ showSkeleton, groupBy, isPivot, vuln, entity = 'vulns' }) => {
  const dispatch = useDispatch();
  const isQuickPlannerOpen = useSelector(selectShowQuickPlanner);
  const pathname = useSelector(selectPathname);
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const workspaces = useSelector(selectWorkspaces);
  const vulnValue = vuln?.value ?? vuln;
  const isExploreRoute = Boolean(matchPath(pathname, {
    path: '/vulns/:ws/groups/:groupId',
    exact: true,
    strict: false
  }));

  const isAutomatic = get(vulnValue, 'is_automatic', null);
  const groupId = get(vulnValue, 'group_id', null);
  const groupVulnId = get(vulnValue, 'group_vuln_id', null);
  const isMain = get(vulnValue, 'is_main', null);
  const vulnId = getVulnId(vuln);
  const isGroupHeader = isMain === true;
  const hasGroupId = groupId !== null;

  const showGoTo = !isExploreRoute && isGroupHeader && hasGroupId;
  const groupHeaderId = groupVulnId ?? vulnId;
  const exploreGroupId = groupId ?? groupHeaderId;

  const valName = (get(vulnValue, 'name', '') || '').trim();
  const workspaceGroupByRaw = (Array.isArray(workspaces) ? workspaces : [])
    .find((ws) => ws?.name === currentWorkspace)?.group_by;
  const normalizedWorkspaceGroupBy = String(workspaceGroupByRaw ?? '').trim().toLowerCase();
  const groupByCriterion = (
    isAutomatic === true &&
    normalizedWorkspaceGroupBy &&
    normalizedWorkspaceGroupBy !== 'none' &&
    normalizedWorkspaceGroupBy !== 'null'
  )
    ? normalizedWorkspaceGroupBy
    : '';

  const groupCount = vuln._groupCount;
  const apiGroupCount = get(vulnValue, 'group_count', null);
  const displayGroupCount = (isGroupHeader && typeof apiGroupCount === 'number')
    ? apiGroupCount
    : groupCount;
  let groupVulnsCount = null;
  if (typeof apiGroupCount === 'number') groupVulnsCount = apiGroupCount;
  else if (typeof groupCount === 'number') groupVulnsCount = groupCount;
  const vulnName = get(vulnValue, 'name', '');

  const groupTitle = get(vulnValue, 'group_title', '');
  let displayName = vulnName;
  if (!isExploreRoute && isGroupHeader) {
    displayName = `${valName} (${displayGroupCount ?? 1})`;
  }

  const handleClick = (e) => {
    if (isQuickPlannerOpen) return;
    dispatch(unSelectAll());
    dispatch(selectRow(e, vuln));
    if (isExploreRoute) {
      dispatch(loadVulnerabilityDetail(vulnId, false));
      return;
    }
    dispatch(redirectToVulnDetail(vulnId));
  };

  const handleExploreClick = (e) => {
    e.stopPropagation();
    if (!exploreGroupId) return;
    dispatch(exploreGroup({
      entity,
      groupId: exploreGroupId,
      groupHeaderId,
      groupDisplayName: vulnName,
      groupBadgeTitle: groupTitle,
      groupByCriterion,
      groupVulnsCount,
      isAutomaticGroup: isAutomatic === true,
      source: 'manage_table_name',
      mode: 'explore'
    }));
  };

  const [, drag, preview] = useDrag(() => ({
    type: itemTypes.VULN,
    item: vuln,
    collect: () => ({ canDrag: isQuickPlannerOpen }),
    canDrag: isQuickPlannerOpen
  }), [vuln, isQuickPlannerOpen]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  if (showSkeleton) return <Skeleton />;
  if (groupBy && isPivot) return null;

  return (
    <Wrapper ref={ drag } $isQuickPlannerOpen={ isQuickPlannerOpen }>
      <Text confirmed={ vuln.confirmed } onClick={ handleClick } $isQuickPlannerOpen={ isQuickPlannerOpen }>
        {displayName}
      </Text>
      <Actions>
        {showGoTo && (
          <GoToButton onClick={ handleExploreClick } title="Explore Group">
            <GoToIcon />
          </GoToButton>
        )}
      </Actions>
    </Wrapper>
  );
};

export default Name;
